import React, { useEffect, useState } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { Modal, Badge, Toggle } from '../components/ui';

const ROLES = ['admin','editor','contributor','viewer'];
const ROLE_DESC = { admin:'Full access — all modules, settings, and user management', editor:'Add and edit records — no settings access, no deletes', contributor:'Add notes and tasks to existing records', viewer:'Read-only access across all modules' };

function TeamModal({ team, onSave, onClose }) {
  const [form, setForm] = useState({ name:'',description:'',role:'viewer',notify_opportunity_updates:1,notify_contract_updates:1,notify_contract_expiry:1, ...team });
  const [saving, setSaving] = useState(false); const [error, setError] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.name) return setError('Team name required');
    setSaving(true);
    try {
      const r = team?.id ? await api.put(`/auth/teams/${team.id}`, form) : await api.post('/auth/teams', form);
      onSave(r.data);
    } catch(e) { setError(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={team?.id ? 'Edit Team' : 'New Team'} onClose={onClose}>
      <div className="modal-body">
        {error && <div className="error-msg">{error}</div>}
        <div className="form-group"><label className="form-label">Team Name *</label><input className="form-input" value={form.name} onChange={set('name')} /></div>
        <div className="form-group"><label className="form-label">Description</label><input className="form-input" value={form.description} onChange={set('description')} /></div>
        <div className="form-group"><label className="form-label">Role (applies to all team members)</label>
          <select className="form-select" value={form.role} onChange={set('role')}>
            {ROLES.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase()+r.slice(1)}</option>)}
          </select>
          <div style={{ fontSize:12,color:'var(--gray-500)',marginTop:4 }}>{ROLE_DESC[form.role]}</div>
        </div>
        <div style={{ marginTop:16 }}>
          {[
            { key:'notify_opportunity_updates', label:'Opportunity updates (notes & stage changes)' },
            { key:'notify_contract_updates', label:'Contract updates (notes & status changes)' },
            { key:'notify_contract_expiry', label:'Contract expiry reminders (≤30 days)' },
          ].map(({ key, label }) => (
            <div key={key} className="checklist-row">
              <span className="checklist-label">{label}</span>
              <Toggle checked={!!form[key]} onChange={v => setForm(f => ({ ...f, [key]: v ? 1 : 0 }))} />
            </div>
          ))}
        </div>
      </div>
      <div className="modal-footer">
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Team'}</button>
      </div>
    </Modal>
  );
}

function UserModal({ editUser, teams, onSave, onClose }) {
  const [form, setForm] = useState({ name:'',email:'',password:'',role:'viewer',team_id:'', ...editUser });
  const [saving, setSaving] = useState(false); const [error, setError] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const isEdit = !!editUser?.id;

  const handleSave = async () => {
    if (!form.name || !form.email || (!isEdit && !form.password)) return setError('All fields required');
    setSaving(true);
    try {
      const r = isEdit ? await api.put(`/auth/users/${editUser.id}`, form) : await api.post('/auth/users', form);
      onSave(r.data);
    } catch(e) { setError(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={isEdit ? 'Edit User' : 'Add User'} onClose={onClose}>
      <div className="modal-body">
        {error && <div className="error-msg">{error}</div>}
        <div className="form-group"><label className="form-label">Full Name</label><input className="form-input" value={form.name} onChange={set('name')} /></div>
        <div className="form-group"><label className="form-label">Email</label><input className="form-input" type="email" value={form.email} onChange={set('email')} /></div>
        {!isEdit && <div className="form-group"><label className="form-label">Password</label><input className="form-input" type="password" value={form.password} onChange={set('password')} /></div>}
        <div className="form-group"><label className="form-label">Team</label>
          <select className="form-select" value={form.team_id} onChange={set('team_id')}>
            <option value="">No team</option>
            {teams.map(t => <option key={t.id} value={t.id}>{t.name} ({t.role})</option>)}
          </select>
        </div>
        <div className="form-group"><label className="form-label">Role Override (overrides team role if set)</label>
          <select className="form-select" value={form.role} onChange={set('role')}>
            {ROLES.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase()+r.slice(1)}</option>)}
          </select>
        </div>
      </div>
      <div className="modal-footer">
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : isEdit ? 'Update User' : 'Create User'}</button>
      </div>
    </Modal>
  );
}

function TierModal({ tier, onSave, onClose }) {
  const [form, setForm] = useState({ tier_name:'', sort_order:0, active:true, baseline_base:'', ...tier, baseline_base: tier?.baseline_base ?? '' });
  const [saving, setSaving] = useState(false); const [error, setError] = useState('');
  const isEdit = !!tier?.id;

  const handleSave = async () => {
    if (!form.tier_name?.trim()) return setError('Tier name required');
    setSaving(true);
    try {
      const body = {
        tier_name: form.tier_name.trim(),
        sort_order: Number(form.sort_order) || 0,
        active: !!form.active,
        baseline_base: form.baseline_base === '' || form.baseline_base == null ? null : Number(form.baseline_base),
      };
      const r = isEdit ? await api.put(`/comp-plan/tiers/${tier.id}`, body) : await api.post('/comp-plan/tiers', body);
      onSave(r.data);
    } catch(e) { setError(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={isEdit ? 'Edit Tier' : 'New Tier'} onClose={onClose}>
      <div className="modal-body">
        {error && <div className="error-msg">{error}</div>}
        <div className="form-group"><label className="form-label">Tier Name *</label><input className="form-input" value={form.tier_name} onChange={e => setForm(f => ({...f, tier_name:e.target.value}))} placeholder="e.g. Senior Architect" /></div>
        <div className="form-group"><label className="form-label">Sort Order</label><input className="form-input" type="number" value={form.sort_order} onChange={e => setForm(f => ({...f, sort_order:e.target.value}))} /></div>
        <div className="form-group">
          <label className="form-label">Internal Baseline Salary ($)</label>
          <input className="form-input" type="number" value={form.baseline_base} onChange={e => setForm(f => ({...f, baseline_base:e.target.value}))} placeholder="e.g. 135000 (leave blank for no baseline)" />
          <div style={{ fontSize:11, color:'var(--gray-500)', marginTop:4 }}>Used to auto-fill Internal FTE positions for this tier. Editable per position.</div>
        </div>
        {isEdit && (
          <div className="checklist-row"><span className="checklist-label">Active</span>
            <Toggle checked={!!form.active} onChange={v => setForm(f => ({ ...f, active: v }))} />
          </div>
        )}
      </div>
      <div className="modal-footer">
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Tier'}</button>
      </div>
    </Modal>
  );
}

function CompPlanTab() {
  const [tiers, setTiers] = useState([]);
  const [showModal, setShowModal] = useState(false); const [editTier, setEditTier] = useState(null);
  const [error, setError] = useState('');

  const load = () => api.get('/comp-plan/tiers').then(r => setTiers(r.data)).catch(e => setError(e.response?.data?.error || 'Failed to load tiers'));
  useEffect(() => { load(); }, []);

  const handleDeactivate = async (t) => {
    if (!window.confirm(`Deactivate tier "${t.tier_name}"? It will be hidden but historical positions referencing it remain intact.`)) return;
    try { await api.delete(`/comp-plan/tiers/${t.id}`); load(); }
    catch(e) { setError(e.response?.data?.error || 'Failed'); }
  };

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <span className="card-title">Comp Plan Tiers</span>
          <div style={{ fontSize:12, color:'var(--gray-500)', marginTop:2 }}>
            Role tier labels for budgeting and grouping. No salary data is stored here.
          </div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => { setEditTier(null); setShowModal(true); }}>+ New Tier</button>
      </div>
      {error && <div className="error-msg" style={{ margin:14 }}>{error}</div>}
      <table>
        <thead><tr>
          <th style={{ width:80 }}>Sort</th>
          <th>Tier Name</th>
          <th style={{ width:160 }}>Internal Baseline</th>
          <th style={{ width:100 }}>Active</th>
          <th style={{ width:140 }}></th>
        </tr></thead>
        <tbody>
          {tiers.length === 0 && <tr><td colSpan={5}><div className="empty-state" style={{ padding:20 }}><div className="empty-state-text">No tiers yet</div></div></td></tr>}
          {tiers.map(t => (
            <tr key={t.id}>
              <td>{t.sort_order}</td>
              <td style={{ fontWeight: t.active ? 500 : 400, color: t.active ? 'inherit' : 'var(--gray-400)' }}>
                {t.tier_name}
                {!t.active && <span style={{ fontSize:11,color:'var(--gray-400)',marginLeft:8 }}>(inactive)</span>}
              </td>
              <td style={{ fontFamily:'SF Mono, Monaco, monospace', fontSize:13 }}>
                {t.baseline_base != null ? `$${Number(t.baseline_base).toLocaleString()}` : <span style={{ color:'var(--gray-400)' }}>—</span>}
              </td>
              <td>{t.active ? <span style={{ color:'var(--success)',fontSize:12,fontWeight:600 }}>Active</span> : <span style={{ color:'var(--gray-400)',fontSize:12 }}>Inactive</span>}</td>
              <td style={{ display:'flex', gap:6 }}>
                <button className="btn btn-secondary btn-sm btn-icon" onClick={() => { setEditTier(t); setShowModal(true); }}>✏️</button>
                {t.active && <button className="btn btn-secondary btn-sm" onClick={() => handleDeactivate(t)} style={{ color:'var(--danger)' }}>Deactivate</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {showModal && <TierModal tier={editTier} onSave={() => { setShowModal(false); load(); }} onClose={() => setShowModal(false)} />}
    </div>
  );
}

function formatConfigValue(value, value_type) {
  if (value_type === 'pct')    return `${(parseFloat(value) * 100).toFixed(2)}%`;
  if (value_type === 'dollar') return `$${parseFloat(value).toLocaleString()}`;
  if (value_type === 'int')    return parseInt(value, 10).toLocaleString();
  return value;
}

function ConfigTab() {
  const [rows, setRows] = useState([]);
  const [editKey, setEditKey] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = () => api.get('/config').then(r => setRows(r.data)).catch(e => setError(e.response?.data?.error || 'Failed to load config'));
  useEffect(() => { load(); }, []);

  const startEdit = (row) => {
    setError('');
    setEditKey(row.key);
    // For pct, show as percentage in input (0.05 → "5"); on save convert back.
    setEditValue(row.value_type === 'pct' ? String(parseFloat(row.value) * 100) : String(row.value));
  };

  const cancelEdit = () => { setEditKey(null); setEditValue(''); };

  const saveEdit = async (row) => {
    setSaving(true); setError('');
    try {
      let outValue = editValue;
      if (row.value_type === 'pct') {
        const num = parseFloat(editValue);
        if (Number.isNaN(num)) throw new Error('Invalid number');
        outValue = String(num / 100);
      } else if (row.value_type === 'int') {
        const num = parseInt(editValue, 10);
        if (Number.isNaN(num)) throw new Error('Invalid integer');
        outValue = String(num);
      } else if (row.value_type === 'dollar') {
        const num = parseFloat(editValue);
        if (Number.isNaN(num)) throw new Error('Invalid number');
        outValue = String(num);
      }
      await api.put(`/config/${row.key}`, { value: outValue });
      setEditKey(null); setEditValue('');
      load();
    } catch(e) { setError(e.response?.data?.error || e.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const inputSuffix = (vt) => vt === 'pct' ? '%' : vt === 'dollar' ? '$' : '';

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <span className="card-title">Configuration Settings</span>
          <div style={{ fontSize:12, color:'var(--gray-500)', marginTop:2 }}>
            Drives margin calculation defaults. Changes apply to all future calculations.
          </div>
        </div>
      </div>
      {error && <div className="error-msg" style={{ margin:14 }}>{error}</div>}
      <table>
        <thead><tr><th style={{ width:200 }}>Key</th><th>Description</th><th style={{ width:220 }}>Value</th><th style={{ width:140 }}></th></tr></thead>
        <tbody>
          {rows.map(row => {
            const editing = editKey === row.key;
            return (
              <tr key={row.key}>
                <td style={{ fontFamily:'SF Mono, Monaco, monospace', fontSize:12 }}>{row.key}</td>
                <td style={{ fontSize:13 }}>{row.description}</td>
                <td>
                  {editing ? (
                    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                      {row.value_type === 'dollar' && <span style={{ color:'var(--gray-500)' }}>$</span>}
                      <input className="form-input" style={{ width:120 }} value={editValue} onChange={e => setEditValue(e.target.value)} autoFocus />
                      {row.value_type === 'pct' && <span style={{ color:'var(--gray-500)' }}>%</span>}
                    </div>
                  ) : (
                    <span style={{ fontFamily:'SF Mono, Monaco, monospace', fontWeight:600 }}>{formatConfigValue(row.value, row.value_type)}</span>
                  )}
                </td>
                <td>
                  {editing ? (
                    <div style={{ display:'flex', gap:6 }}>
                      <button className="btn btn-primary btn-sm" onClick={() => saveEdit(row)} disabled={saving}>{saving ? '…' : 'Save'}</button>
                      <button className="btn btn-secondary btn-sm" onClick={cancelEdit}>Cancel</button>
                    </div>
                  ) : (
                    <button className="btn btn-secondary btn-sm btn-icon" onClick={() => startEdit(row)}>✏️</button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function AuditLogTab() {
  const [data, setData] = useState({ rows: [], total: 0, limit: 50, offset: 0 });
  const [error, setError] = useState('');

  const load = (offset = 0) => {
    api.get('/comp-plan/audit', { params: { limit: 50, offset } })
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.error || 'Failed to load audit log'));
  };
  useEffect(() => { load(0); }, []);

  const ACTION_LABELS = {
    granted_comp_plan_access: 'Granted Comp Plan Access',
    revoked_comp_plan_access: 'Revoked Comp Plan Access',
    granted_staffing_access:  'Granted Staffing Access',
    revoked_staffing_access:  'Revoked Staffing Access',
    granted_owner: 'Granted Owner',
    revoked_owner: 'Revoked Owner',
  };

  const totalPages  = Math.max(1, Math.ceil(data.total / data.limit));
  const currentPage = Math.floor(data.offset / data.limit) + 1;

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <span className="card-title">Audit Log</span>
          <div style={{ fontSize:12, color:'var(--gray-500)', marginTop:2 }}>
            All grants and revocations of Owner and Comp Plan Access privileges.
          </div>
        </div>
      </div>
      {error && <div className="error-msg" style={{ margin:14 }}>{error}</div>}
      <table>
        <thead>
          <tr>
            <th style={{ width:170 }}>Time</th>
            <th>Action</th>
            <th>Target User</th>
            <th>Performed By</th>
            <th style={{ width:120 }}>Change</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.length === 0 && <tr><td colSpan={5}><div className="empty-state" style={{ padding:20 }}><div className="empty-state-text">No audit entries yet</div></div></td></tr>}
          {data.rows.map(r => {
            const granted = r.action.startsWith('granted');
            return (
              <tr key={r.id}>
                <td style={{ fontSize:12 }}>{new Date(r.occurred_at).toLocaleString()}</td>
                <td>
                  <span style={{
                    display:'inline-flex', padding:'2px 9px', borderRadius:20, fontSize:11, fontWeight:600,
                    background: granted ? 'var(--success-light)' : 'var(--danger-light)',
                    color:      granted ? 'var(--success)'       : 'var(--danger)',
                  }}>
                    {ACTION_LABELS[r.action] || r.action}
                  </span>
                </td>
                <td style={{ fontSize:13 }}>{r.target_user_email || '—'}</td>
                <td style={{ fontSize:13 }}>{r.performed_by_email || '—'}</td>
                <td style={{ fontSize:12, color:'var(--gray-500)', fontFamily:'SF Mono, Monaco, monospace' }}>
                  {r.old_value} → {r.new_value}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {data.total > data.limit && (
        <div className="pagination">
          <button className="page-btn" disabled={data.offset === 0}
                  onClick={() => load(Math.max(0, data.offset - data.limit))}>← Prev</button>
          <span className="page-info">Page {currentPage} of {totalPages} ({data.total} entries)</span>
          <button className="page-btn" disabled={data.offset + data.limit >= data.total}
                  onClick={() => load(data.offset + data.limit)}>Next →</button>
        </div>
      )}
    </div>
  );
}

function TabStrip({ tabs, active, onChange }) {
  return (
    <div style={{ display:'flex', gap:0, borderBottom:'1px solid var(--border-color)', marginBottom:20 }}>
      {tabs.map(t => (
        <button
          key={t.key}
          onClick={() => onChange(t.key)}
          style={{
            background:'none',
            border:'none',
            borderBottom: active === t.key ? '2px solid var(--primary)' : '2px solid transparent',
            color: active === t.key ? 'var(--primary)' : 'var(--text-secondary)',
            fontWeight: active === t.key ? 600 : 500,
            fontSize: 13,
            padding: '10px 16px',
            cursor: 'pointer',
            transition: 'all 0.15s',
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export default function Settings() {
  const { user, refreshMe } = useAuth();
  const [tab, setTab] = useState('general');
  const [teams, setTeams] = useState([]); const [users, setUsers] = useState([]);
  const [showTeamModal, setShowTeamModal] = useState(false); const [editTeam, setEditTeam] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false); const [editUser, setEditUser] = useState(null);
  const [pwForm, setPwForm] = useState({ currentPassword:'',newPassword:'',confirm:'' });
  const [pwMsg, setPwMsg] = useState(''); const [pwError, setPwError] = useState(''); const [pwSaving, setPwSaving] = useState(false);
  const [permError, setPermError] = useState('');
  const isAdmin = user?.role === 'admin';
  const canViewCompPlan = !!user?.can_view_comp_plan;
  const isOwner = !!user?.is_owner;

  const loadTeams = () => api.get('/auth/teams').then(r => setTeams(r.data)).catch(()=>{});
  const loadUsers = () => isAdmin && api.get('/auth/users').then(r => setUsers(r.data)).catch(()=>{});

  useEffect(() => { loadTeams(); loadUsers(); }, [user]);

  const handleCompPlanToggle = async (u, newVal) => {
    setPermError('');
    try {
      await api.put(`/auth/users/${u.id}/comp-plan-access`, { can_view_comp_plan: newVal });
      await loadUsers();
      if (u.id === user.id) await refreshMe();
    } catch (e) { setPermError(e.response?.data?.error || 'Failed to update access'); }
  };

  const handleStaffingToggle = async (u, newVal) => {
    setPermError('');
    try {
      await api.put(`/auth/users/${u.id}/staffing-access`, { can_view_staffing: newVal });
      await loadUsers();
      if (u.id === user.id) await refreshMe();
    } catch (e) { setPermError(e.response?.data?.error || 'Failed to update access'); }
  };

  const handleOwnerToggle = async (u, newVal) => {
    setPermError('');
    const action = newVal ? 'grant Owner privileges to' : 'revoke Owner privileges from';
    const extra  = newVal ? ' Owners can grant or revoke Comp Plan Access and Owner status for any user.' :
                  (u.id === user.id ? ' WARNING: revoking your own Owner status means only another Owner (or a manual psql update) can restore it.' : '');
    if (!window.confirm(`Are you sure you want to ${action} ${u.name}?${extra}`)) return;
    try {
      await api.put(`/auth/users/${u.id}/owner`, { is_owner: newVal });
      await loadUsers();
      if (u.id === user.id) await refreshMe();
    } catch (e) { setPermError(e.response?.data?.error || 'Failed to update owner status'); }
  };

  const handlePasswordChange = async () => {
    setPwMsg(''); setPwError('');
    if (pwForm.newPassword !== pwForm.confirm) return setPwError('Passwords do not match');
    if (pwForm.newPassword.length < 6) return setPwError('Password must be at least 6 characters');
    setPwSaving(true);
    try { await api.put(`/auth/users/${user.id}/password`, { currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword }); setPwMsg('Password updated'); setPwForm({ currentPassword:'',newPassword:'',confirm:'' }); }
    catch(e) { setPwError(e.response?.data?.error || 'Failed'); }
    finally { setPwSaving(false); }
  };

  const tabs = [
    { key:'general', label:'General' },
    ...(canViewCompPlan ? [{ key:'comp-plan', label:'Comp Plan' }, { key:'config', label:'Config' }] : []),
    ...(isOwner ? [{ key:'audit', label:'Audit Log' }] : []),
  ];

  return (
    <>
      <div className="page-header"><span className="page-title">Settings</span></div>
      <div className="page-body" style={{ maxWidth:860 }}>

        <TabStrip tabs={tabs} active={tab} onChange={setTab} />

        {tab === 'general' && (
          <>
            {/* Teams */}
            {isAdmin && (
              <div className="card" style={{ marginBottom:20 }}>
                <div className="card-header">
                  <div><span className="card-title">Teams</span><div style={{ fontSize:12,color:'var(--gray-500)',marginTop:2 }}>Teams determine role-based access and notification preferences</div></div>
                  <button className="btn btn-primary btn-sm" onClick={() => { setEditTeam(null); setShowTeamModal(true); }}>+ New Team</button>
                </div>
                <table>
                  <thead><tr><th>Team Name</th><th>Role</th><th>Members</th><th>Opp Notif.</th><th>Contract Notif.</th><th>Expiry Notif.</th><th></th></tr></thead>
                  <tbody>
                    {teams.length === 0 && <tr><td colSpan={7}><div className="empty-state" style={{ padding:20 }}><div className="empty-state-text">No teams yet</div></div></td></tr>}
                    {teams.map(t => (
                      <tr key={t.id}>
                        <td><div style={{ fontWeight:500 }}>{t.name}</div>{t.description && <div style={{ fontSize:11,color:'var(--gray-500)' }}>{t.description}</div>}</td>
                        <td><Badge status={t.role} /></td>
                        <td>{t.member_count}</td>
                        <td>{t.notify_opportunity_updates ? '✅' : '—'}</td>
                        <td>{t.notify_contract_updates ? '✅' : '—'}</td>
                        <td>{t.notify_contract_expiry ? '✅' : '—'}</td>
                        <td>
                          <button className="btn btn-secondary btn-sm btn-icon" onClick={() => { setEditTeam(t); setShowTeamModal(true); }}>✏️</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Users */}
            {isAdmin && (
              <div className="card" style={{ marginBottom:20 }}>
                <div className="card-header">
                  <span className="card-title">Users</span>
                  <button className="btn btn-primary btn-sm" onClick={() => { setEditUser(null); setShowUserModal(true); }}>+ Add User</button>
                </div>
                {permError && <div className="error-msg" style={{ margin:14 }}>{permError}</div>}
                <table>
                  <thead>
                    <tr>
                      <th>Name</th><th>Email</th><th>Role</th><th>Team</th><th>Status</th>
                      {isOwner && <th style={{ width:110 }}>Comp Plan</th>}
                      {isOwner && <th style={{ width:100 }}>Staffing</th>}
                      {isOwner && <th style={{ width:90 }}>Owner</th>}
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.id}>
                        <td style={{ fontWeight:500 }}>{u.name}{u.id===user?.id&&<span style={{ fontSize:11,color:'var(--primary)',marginLeft:6 }}>(you)</span>}</td>
                        <td style={{ fontSize:13 }}>{u.email}</td>
                        <td><Badge status={u.role} /></td>
                        <td style={{ fontSize:13 }}>{u.team_name||'—'}</td>
                        <td>{u.is_active ? <span style={{ color:'var(--success)',fontSize:12,fontWeight:600 }}>Active</span> : <span style={{ color:'var(--gray-400)',fontSize:12 }}>Inactive</span>}</td>
                        {isOwner && (
                          <td onClick={e => e.stopPropagation()}>
                            <Toggle checked={!!u.can_view_comp_plan} onChange={v => handleCompPlanToggle(u, v)} />
                          </td>
                        )}
                        {isOwner && (
                          <td onClick={e => e.stopPropagation()}>
                            <Toggle checked={!!u.can_view_staffing} onChange={v => handleStaffingToggle(u, v)} />
                          </td>
                        )}
                        {isOwner && (
                          <td onClick={e => e.stopPropagation()}>
                            <Toggle checked={!!u.is_owner} onChange={v => handleOwnerToggle(u, v)} />
                          </td>
                        )}
                        <td><button className="btn btn-secondary btn-sm btn-icon" onClick={() => { setEditUser(u); setShowUserModal(true); }}>✏️</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Change password - only show for local (non-SSO) accounts */}
            {!user?.sso && <div className="card">
              <div className="card-header"><span className="card-title">Change Password</span></div>
              <div className="card-body">
                {pwMsg && <div className="success-msg">{pwMsg}</div>}
                {pwError && <div className="error-msg">{pwError}</div>}
                <div style={{ maxWidth:360 }}>
                  <div className="form-group"><label className="form-label">Current Password</label><input className="form-input" type="password" value={pwForm.currentPassword} onChange={e => setPwForm(f => ({...f,currentPassword:e.target.value}))} /></div>
                  <div className="form-group"><label className="form-label">New Password</label><input className="form-input" type="password" value={pwForm.newPassword} onChange={e => setPwForm(f => ({...f,newPassword:e.target.value}))} /></div>
                  <div className="form-group"><label className="form-label">Confirm New Password</label><input className="form-input" type="password" value={pwForm.confirm} onChange={e => setPwForm(f => ({...f,confirm:e.target.value}))} /></div>
                  <button className="btn btn-primary" onClick={handlePasswordChange} disabled={pwSaving}>{pwSaving?'Saving...':'Update Password'}</button>
                </div>
              </div>
            </div>}

            {/* App info */}
            <div className="card" style={{ marginTop:20,padding:'14px 20px' }}>
              <div style={{ fontSize:13,color:'var(--gray-500)' }}>
                <strong>Pulse CRM v2.1</strong> · PostgreSQL + Node.js + React · Logged in as <strong>{user?.name}</strong> · Role: <strong>{user?.role}</strong>{user?.team_name && ` · Team: ${user.team_name}`}
                <div style={{ marginTop:6, color:'var(--gray-400)', fontSize:11 }}>© {new Date().getFullYear()} iShift.io · All rights reserved</div>
              </div>
            </div>
          </>
        )}

        {tab === 'comp-plan' && canViewCompPlan && <CompPlanTab />}
        {tab === 'config' && canViewCompPlan && <ConfigTab />}
        {tab === 'audit' && isOwner && <AuditLogTab />}
      </div>

      {showTeamModal && <TeamModal team={editTeam} onSave={() => { setShowTeamModal(false); loadTeams(); }} onClose={() => setShowTeamModal(false)} />}
      {showUserModal && <UserModal editUser={editUser} teams={teams} onSave={() => { setShowUserModal(false); loadUsers(); }} onClose={() => setShowUserModal(false)} />}
    </>
  );
}
